import {
  EditNotificationsValues,
  NotificationItem,
  NotificationItemComment,
} from '@notifications/hoc/center/types';
import { Category } from '@ui/src/components/accounts-selector/types';
import {
  Account,
  AccountBalance,
  AccountType,
  GeneralLedgerTransaction,
  GeneralLedgerTransactionFormValues,
  Response,
  ValidatorsProps,
  TransactionAML,
  TransactionStatusLog,
  TransactionWarning,
  BaseTransaction,
  ExchangeTransaction,
  NostroTransaction,
  apiAbortController,
  Document,
  DocumentStatus,
  DocumentType,
  Channel,
  ChangeLog,
  DocumentComment,
} from '@ui/src';
import {
  AccountsCurrencies,
  AccountsCurrenciesFormValues,
  AccountsFlows,
  AccountsFlowsFormValues,
  AffilatedCompany,
  AffilatedCompanyFormValues,
  AllowedCategory,
  CRSAndFatca,
  CRSAndFatcaFormValues,
  DictionaryItem,
  DocumentsStats,
  ExpectedFundsOrigins,
  ExpectedFundsOriginsFormValues,
} from '../../../new-application/types';
import api from '../../../../api/api';
import {
  CustomerProps,
  Tariff,
  CustomFeeFormValues,
  FeeItem,
  StatementListItemProps,
  CustomerDocument,
  CustomerStats,
  CustomerDocumentStatus,
  PspPartner,
  Balance,
  TopUpVisibility,
  TopUpInstructions,
  CustomerAgreement,
  ChatBody,
  ProblemTransactionCofProps,
  UploadResponse,
  Beneficiary,
  BeneficiaryStatus,
  BeneficiaryStatusLogs,
  BeneficiaryContract,
  BeneficiaryContractFormValues,
  ExternalAccount,
  ExternalAccountFormValues,
  ExternalPspPartner,
  CustomMargin,
  RateSource,
  MarginAmount,
  MarginFormValues,
  Questionnarie,
  CustomerScore,
  PspIntegration,
  ExternalAccountWithIntegrationFormValues,
  PspIntegrationOrganization,
  ExternalOrganizationFormValues,
  ExternalOrganizationAccount,
  ExternalAccountToClientAccountFormValues,
  ExternalAccountTopUpOption,
  LinkedClient,
  ExternalAccountTopUpOptionFormValues,
} from '../../types';
import { SumSub } from '../../../sumsub/types';

export const customersComponentApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCustomersTariffsList: build.query<Response<Tariff>, string>({
      query: (query) => ({
        url: `/v1/tariffs?all=true&status=PUBLISHED${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    assignTariffToCustomer: build.mutation<
      {
        data: CustomerProps;
      },
      { body: { tariffId: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomFee: build.mutation<
      {
        data: FeeItem;
      },
      { body: CustomFeeFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/fees`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomFee: build.mutation<
      {
        data: FeeItem;
      },
      { feeId: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/fees/${data.feeId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTargetAccount: build.query<{ data: { fullNumber: string } }, string>({
      query: (id) => ({
        url: `/v1/accounts/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersBaseCurrency: build.query<{ data: { id: string } }, void>({
      query: () => ({
        url: `/v1/currencies/base`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountBalanceById: build.query<
      Response<AccountBalance>,
      {
        id: string;
        query?: string;
        typeAcc: AccountType.CREDIT | AccountType.DEBIT;
      }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true${data.query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersGLTransactionValidators: build.query<
      {
        data: {
          createGeneralLedgerTransactionSchema: ValidatorsProps;
        };
      },
      void
    >({
      query: () => ({
        url: '/v1/general-ledger/transactions/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersGLTransaction: build.mutation<
      { data: GeneralLedgerTransaction },
      GeneralLedgerTransactionFormValues
    >({
      query: (body) => ({
        url: '/v1/general-ledger/transactions',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountsList: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAccountPrimaries: build.query<Response<any>, void>({
      query: () => ({
        url: '/v1/accounts-primaries?all=true&groupId=21ac4088-1f26-4390-b65a-42a63eac4889',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountsCategories: build.query<Response<Category>, string>({
      query: (query) => ({
        url: `/v1/accounts-categories?${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOverviewStatementsList: build.query<
      Response<StatementListItemProps>,
      { query: string; id: string }
    >({
      query: ({ query, id }) => ({
        url: `/v1/clients-organizations/${id}/statements-items${query.replace(
          'take=20',
          'take=15',
        )}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportSelectedOverviewStatements: build.query<
      string,
      { query: string; id: string }
    >({
      query: ({ query, id }) => ({
        url: `/v1/clients-organizations/${id}/statements-items?format=csv&all=true${query}`,
        responseHandler: (response) => response.text(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionAml: build.query<Response<TransactionAML>, string>({
      query: (id) => ({
        url: `/v1/scores-results?transactionId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTransactionLogs: build.query<
      Response<TransactionStatusLog>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-queue-items?transactionId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getSumSubToken: build.query<{ data: SumSub }, string>({
      query: (id) => ({
        url: `/v1/clients-applications/${id}/sumsub-token`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getExpiredDocuments: build.query<
      Response<CustomerDocument>,
      {
        id: string;
        query?: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents${data?.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDocuments: build.query<
      Response<CustomerDocument>,
      {
        id: string;
        take: string;
        sort: string;
        sortProp: string;
        categoryId?: string;
        cursor?: string;
        name?: string;
        status?: string[];
        reload?: boolean;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents?take=${
          data.take
        }&sortProp=${data.sortProp}&sortDir=${data.sort}${
          data?.categoryId ? `&documentCategoryId=${data.categoryId}` : ''
        }${data?.cursor ? `&cursor=${data.cursor}` : ''}${
          data?.name ? `&name=${data.name}` : ''
        }${
          data?.status && data?.status?.length > 0
            ? `&status=${data.status.join('&status=')}`
            : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDocument: build.query<
      { data: CustomerDocument },
      { id: string; documentId: string }
    >({
      query: ({ id, documentId }) => ({
        url: `/v1/clients-organizations/${id}/documents/${documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerStats: build.query<
      { data: CustomerStats },
      { id: string; query?: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/${id}/stats${query ? `?${query}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCustomerDocuments: build.mutation<
      {
        data: CustomerDocument;
      },
      {
        id: string;
        body: {
          uploadId: string;
          name: string;
          documentCategoryId?: string;
          expirationDate?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomerDocument: build.mutation<
      {
        data: CustomerDocument;
      },
      {
        id: string;
        documentId: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/${data.documentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    renameCustomerDocument: build.mutation<
      {
        data: CustomerDocument;
      },
      {
        id: string;
        documentId: string;
        body: {
          name: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/${data.documentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchDeleteCustomerDocuments: build.mutation<
      {
        data: CustomerDocument;
      },
      {
        id: string;
        documentId: string[];
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/batch-delete`,
        method: 'DELETE',
        body: {
          documentId: data.documentId,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCustomerDocumentsStatus: build.mutation<
      any,
      { body: { ids: string[]; status: CustomerDocumentStatus }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeCustomerDocumentsCategory: build.mutation<
      any,
      {
        body: {
          ids: string[];
          category?: string;
          documentCategoryId?: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerIncomingTransaction: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerIncomingTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerIncomingTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-incoming/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerOutgoingTransaction: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerOutgoingTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerOutgoingTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-outgoing/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerInternalTransaction: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerInternalTransactionWarnings: build.query<
      Response<TransactionWarning>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}/warnings?take=100`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerInternalTransactionWarning: build.query<
      { data: TransactionWarning },
      { id: string; warningId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions-internal/${data.id}/warnings/${data.warningId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerGeneralLedgerTransaction: build.query<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerExchangeTransaction: build.query<
      { data: ExchangeTransaction & { exchangeCurrencyFixedSum: string } },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-exchange/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerNostroTransaction: build.query<
      { data: NostroTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-nostro/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTransactionsPspPartners: build.query<
      Response<PspPartner>,
      string
    >({
      query: (query) => ({
        url: `/v1/psp-partners${query}`,
      }),
    }),
    getCustomersGeneralLedgerStatment: build.query<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersInternalStatement: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-internal/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAmlStatement: build.query<Response<TransactionAML>, string>({
      query: (id) => ({
        url: `/v1/scores-results?transactionId=${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersLogStatement: build.query<
      Response<TransactionStatusLog>,
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-queue-items?transactionId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersIncomingStatement: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-incoming/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionInDocument: build.query<{ data: BaseTransaction }, string>({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountBalance: build.query<
      Response<Balance>,
      { id: string; currencyId: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?currencyId=${data.currencyId}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersOutgoingStatement: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-outgoing/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExchangeStatement: build.query<
      { data: BaseTransaction & { exchangeCurrencyFixedSum: string } },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-exchange/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNostroStatement: build.query<
      { data: NostroTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions-nostro/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTopUp: build.query<
      Response<{ companyName: string; id: string; visibleToClients: boolean }>,
      { cursor?: string; companyName?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners?active=true&take=10&visibleToClients=true${
          data.cursor ? `&cursor=${data.cursor}` : ''
        }${data?.companyName ? `&companyName=${data.companyName}` : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTopUpInstructions: build.query<
      { data: TopUpInstructions },
      { id: string; currencyId: string; pspPartnerId: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/top-up?currencyId=${data.currencyId}&pspPartnerId=${data.pspPartnerId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTopUpInstructionPdf: build.query<
      { data: TopUpInstructions },
      { id: string; currencyId: string; pspPartnerId: string; index: number }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/top-up/${data.index}/pdf?currencyId=${data.currencyId}&pspPartnerId=${data.pspPartnerId}`,
        responseHandler: (response) => {
          let responseFormatted;

          if (response.status !== 200) {
            responseFormatted = response.json();
          } else {
            responseFormatted = response.blob();
          }

          return responseFormatted;
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getStatementItemsPdf: build.query<
      { data: any },
      { query: string; type: string }
    >({
      query: (data) => ({
        url: `/v1/statements-items/${data.type}?${data.query}`,
        responseHandler: (response) => {
          let responseFormatted;

          if (response.status !== 200) {
            responseFormatted = response.json();
          } else {
            responseFormatted = response.blob();
          }

          return responseFormatted;
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersStatementPdf: build.query<any, { id: string; query: string }>({
      query: (data) => ({
        url: `/v1/statements-items/${data.id}/pdf?${data.query}`,
        responseHandler: (response) => {
          let responseFormatted;

          if (response.status !== 200) {
            responseFormatted = response.json();
          } else {
            responseFormatted = response.blob();
          }

          return responseFormatted;
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllTopUpVisibility: build.query<
      Response<TopUpVisibility>,
      { organizationId: string; query: string }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/visibility?organizationId=${data.organizationId}${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addTopUpVisibility: build.mutation<
      {
        data: TopUpVisibility;
      },
      {
        id: string;
        body: {
          organizationId: string;
          visible: boolean;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.id}/visibility`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTopUpVisibility: build.mutation<
      {
        data: TopUpVisibility;
      },
      {
        id: string;
        pspId: string;
        body: {
          pspPartnerId: string;
          organizationId: string;
          visible: boolean;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/psp-partners/${data.pspId}/visibility/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerAgreements: build.query<Response<CustomerAgreement>, string>({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/agreements`,
      }),
    }),
    getCustomerUnsignedAgreements: build.query<
      { data: CustomerAgreement[] },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/agreements/unsigned`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerAgreementFile: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/agreements/${id}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionChats: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/channels?transactionId=${id}&all=true&status=OPEN`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createProblemTransactionChat: build.mutation<
      { data: Channel },
      { id: string; body: ChatBody }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/channels`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionSenderClientsOfficias: build.query<
      Response<ProblemTransactionCofProps>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionRecipientClientsOfficias: build.query<
      Response<ProblemTransactionCofProps>,
      string
    >({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    uploadDocument: build.mutation({
      async queryFn(file: File, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('files', file);

        const response = (await fetchWithBQ({
          url: '/v1/uploads',
          method: 'POST',
          body: formData,
        })) as { data: UploadResponse[] };

        return response;
      },
    }),
    getProblemTransactionDocumentById: build.query<
      { data: Document },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionsDocuments: build.query<
      Response<Document>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents${
          data?.cursor ? `?cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    addDocumentToProblemTransaction: build.mutation<
      { data: Document },
      {
        id: string;
        body: {
          name: string;
          uploadId: string;
          organizationId?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeProblemTransactionDocumentStatus: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; status: DocumentStatus } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeProblemTransactionDocumentCategory: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; category: DocumentType } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCommentToProblemTransactionDocument: build.mutation<
      any,
      { id: string; body: { text: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editCommentInProblemTransactionDocument: build.mutation<
      any,
      { id: string; commentId: string; body: { text: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCommentInProblemTransactionDocument: build.mutation<
      any,
      { id: string; commentId: string }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteProblemTransactionDocuments: build.mutation<
      {
        data: { count: number };
      },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/batch-delete`,
        method: 'DELETE',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerAgreement: build.query<{ data: CustomerAgreement }, string>({
      query: (id) => ({
        url: `/v1/agreements/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    resetCustomerPassword: build.mutation<{ data: { token: string } }, string>({
      query: (id) => ({
        url: `/v1/recover`,
        method: 'POST',
        body: {
          userId: id,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeDocumentExpirationDate: build.mutation<
      { data: Document },
      { id: string; body: { id: string; expirationDate: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeBalanceStatus: build.mutation<
      { data: Account },
      { id: string; body: { currencyId: string; active: boolean } }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOngoingMoitoringPdf: build.mutation<
      { data: any },
      { id: string; body: { createdDateFrom?: string; createdDateTo?: string } }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/warnings-groups/export`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerBeneficiaries: build.query<Response<Beneficiary>, string>({
      query: (query) => ({
        url: `/v1/beneficiaries${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerBeneficiary: build.query<{ data: Beneficiary }, string>({
      query: (id) => ({
        url: `/v1/beneficiaries/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    validateCustomerBeneficiary: build.mutation<{ data: Beneficiary }, string>({
      query: (id) => ({
        url: `/v1/beneficiaries/${id}/validate`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerBeneficiaryStatusLog: build.query<
      Response<BeneficiaryStatusLogs>,
      string
    >({
      query: (id) => ({
        url: `/v1/beneficiaries/${id}/logs?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerBeneficiaryContracts: build.query<
      Response<BeneficiaryContract>,
      string
    >({
      query: (id) => ({
        url: `/v1/beneficiaries/${id}/contracts`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerOrganizationAllowedDocumentsCategories: build.query<
      { data: AllowedCategory[] },
      void
    >({
      query: () => ({
        url: '/v1/clients-organizations/documents/allowed-categories',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerOrganizationDocumentsStats: build.query<
      { data: DocumentsStats[] },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}/documents/stats`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateBeneficiaryStatus: build.mutation<
      { data: Beneficiary },
      { id: string; body: { status: BeneficiaryStatus; comment?: string } }
    >({
      query: (data) => ({
        url: `/v1/beneficiaries/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerBeneficiaryContract: build.query<
      { data: BeneficiaryContract },
      { beneficiaryId: string; contractId: string }
    >({
      query: (data) => ({
        url: `/v1/beneficiaries/${data.beneficiaryId}/contracts/${data.contractId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateContractStatus: build.mutation<
      { data: BeneficiaryContract },
      { id: string; contractId: string; body: { status: BeneficiaryStatus } }
    >({
      query: (data) => ({
        url: `/v1/beneficiaries/${data.id}/contracts/${data.contractId}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateContract: build.mutation<
      { data: BeneficiaryContract },
      { id: string; contractId: string; body: BeneficiaryContractFormValues }
    >({
      query: (data) => ({
        url: `/v1/beneficiaries/${data.id}/contracts/${data.contractId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getContractDocumentFile: build.query<
      { data: any },
      { id: string; contractId: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/beneficiaries/${data.id}/contracts/${data.contractId}/supporting-documents/${data.documentId}/file`,
        responseHandler: (response) => {
          let responseFormatted;

          if (response.status !== 200) {
            responseFormatted = response.json();
          } else {
            responseFormatted = response.blob();
          }

          return responseFormatted;
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExternalAccountsList: build.query<
      Response<ExternalAccount>,
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExternalAccount: build.query<{ data: ExternalAccount }, string>(
      {
        query: (id) => ({
          url: `/v1/psp-integrations-accounts/${id}`,
          signal: apiAbortController.getSignalToken(),
        }),
      },
    ),
    getCustomersPspPartners: build.query<Response<ExternalPspPartner>, void>({
      query: () => ({
        url: '/v1/psp-partners?all=true&active=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersExternalAccount: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersExternalAccountStatus: build.mutation<
      { data: { count: number } },
      { ids: string[]; active: boolean }
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/status',
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersOverdraftStatus: build.mutation<
      {
        data: Account & { overdraftEnabled?: boolean };
      },
      {
        id: string;
        accountId: string;
        overdraftEnabled: boolean;
      }
    >({
      query: (body) => ({
        url: `/v1/clients-organizations/${body.id}/accounts/${body.accountId}/overdraft`,
        method: 'PUT',
        body: { overdraftEnabled: body.overdraftEnabled },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersOverdraftBalanceValue: build.mutation<
      {
        data: Balance;
      },
      {
        id: string;
        accountId: string;
        balanceId: string;
        overdraftEnabled: boolean;
        overdraftValue: number;
      }
    >({
      query: (body) => ({
        url: `/v1/clients-organizations/${body.id}/accounts/${body.accountId}/balances/${body.balanceId}/overdraft`,
        method: 'PUT',
        body: {
          overdraftValue: body.overdraftValue,
          overdraftEnabled: body.overdraftEnabled,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTransactionStatementsChangeLogs: build.query<
      Response<ChangeLog>,
      string
    >({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomMargins: build.query<Response<CustomMargin>, string>({
      query: (query) => ({
        url: `/v1/margins${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersRateSources: build.query<Response<RateSource>, void>({
      query: () => ({
        url: '/v1/rates-sources?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    customersCreateAmounts: build.mutation<
      {
        data: CustomMargin;
      },
      { body: { items: MarginAmount[] } }
    >({
      query: (data) => ({
        url: '/v1/margins-amounts/batch-create',
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerMargin: build.query<{ data: CustomMargin }, string>({
      query: (id) => ({
        url: `/v1/margins/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerMargin: build.mutation<
      {
        data: CustomMargin;
      },
      { body: Partial<MarginFormValues>; id: string }
    >({
      query: (data) => ({
        url: `/v1/margins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomerMargin: build.mutation<
      {
        data: CustomMargin;
      },
      Partial<MarginFormValues>
    >({
      query: (body) => ({
        url: '/v1/margins',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerAmount: build.mutation<
      {
        data: CustomMargin;
      },
      { body: MarginAmount; id: string }
    >({
      query: (data) => ({
        url: `/v1/margins-amounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomerAmount: build.mutation<
      {
        data: MarginAmount;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/margins-amounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerDefaultMargin: build.query<{ data: CustomMargin }, void>({
      query: () => ({
        url: '/v1/margins/default',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    restoreCustomMarginToDefault: build.mutation<
      {
        data: CustomMargin;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/margins/${id}/restore`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountsChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersBeneficairyDocumentsCategories: build.query<
      Response<{ name: string; id: string }>,
      void
    >({
      query: () => ({
        url: '/v1/documents-categories?type=BENEFICIARY',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getBeneficiaryDocumentFile: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/uploads/${id}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerQuestionnaire: build.mutation<
      {
        data: Questionnarie;
      },
      { body: Partial<Questionnarie>; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/questionnaire`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    closeOrganization: build.mutation<
      {
        data: CustomerProps;
      },
      { body: { statusChangeReason: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/close`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    reopenOrganization: build.mutation<
      {
        data: CustomerProps;
      },
      { body: { statusChangeReason: string }; id: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/open`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationsList: build.query<
      Response<NotificationItem>,
      string
    >({
      query: (query) => ({
        url: `/v1/notifications/messaging-and-customers${query}${
          query ? '&' : '?'
        }take=20&sortProp=createdAt&sortDir=desc`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationItem: build.query<
      { data: NotificationItem },
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/messaging-and-customers/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationItemComment: build.query<
      Response<NotificationItemComment>,
      string
    >({
      query: (id) => ({
        url: `/v1/notifications/messaging-and-customers/${id}/comments?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCustomersNotificationItemComment: build.mutation<
      { data: NotificationItemComment },
      { id: string; body: { content: string } }
    >({
      query: (data) => ({
        url: `/v1/notifications/messaging-and-customers/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersNotificationIssue: build.mutation<
      { data: NotificationItem },
      {
        id: string;
        body: {
          responsibleId?: string;
          executorId?: string;
          status?: string;
          deadline?: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/messaging-and-customers/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationChangeLogs: build.query<
      Response<ChangeLog>,
      string
    >({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationApplicationEntity: build.query<
      { data: any },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-applications/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationChannelEntity: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/channels/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationOrganizationEntity: build.query<
      { data: any },
      string
    >({
      query: (id) => ({
        url: `/v1/clients-organizations/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchUpdateCustomersNotificationIssue: build.mutation<
      { data: number },
      EditNotificationsValues
    >({
      query: (data) => ({
        url: '/v1/notifications/messaging-and-customers/batch-update',
        method: 'PUT',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersNotificationEmployees: build.query<
      Response<{
        id: string;
        profile: {
          firstName: string;
          lastName: string;
        };
      }>,
      void
    >({
      query: () => ({
        url: `/v1/employees?take=9999`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    batchAddCustomersNotificationItemComment: build.mutation<
      { data: number },
      { ids: string[]; content: string }
    >({
      query: (data) => ({
        url: '/v1/notifications/messaging-and-customers/batch-comments',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersNotificationIssueComment: build.mutation<
      { data: NotificationItemComment },
      {
        id: string;
        commentId: string;
        body: {
          content: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/notifications/messaging-and-customers/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerAllDebitAccountBalances: build.query<
      Response<Balance>,
      { id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerAllCreditAccountBalances: build.query<
      Response<Balance>,
      { id: string }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerStatementChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCommentToProblemTransaction: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        url: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editCommentInProblemTransaction: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        url: string;
        commentId: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCommentInProblemTransaction: build.mutation<
      { data: DocumentComment },
      { id: string; commentId: string; url: string }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getProblemTransactionComments: build.query<
      Response<DocumentComment>,
      { id: string; url: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/${data.url}/${data.id}/comments?take=20${
          data?.cursor ? `&cursor=${data.cursor}` : ''
        }&sortDir=desc&sortProp=createdAt`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomerComments: build.query<
      Response<DocumentComment>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/comments?take=20${
          data?.cursor ? `&cursor=${data.cursor}` : ''
        }&sortDir=desc&sortProp=createdAt`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addCommentToCustomer: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editCommentInCustomer: build.mutation<
      { data: DocumentComment },
      {
        id: string;
        commentId: string;
        body: {
          text: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCommentInCustomer: build.mutation<
      { data: DocumentComment },
      { id: string; commentId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAMLScoresHistory: build.query<
      Response<CustomerScore>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/scores${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAMLScoresHistoryById: build.query<
      { data: CustomerScore },
      { id: string; scoreId: string }
    >({
      query: (data) => ({
        url: `/v1/clients-organizations/${data.id}/scores/${data.scoreId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersPspIntegrations: build.query<Response<PspIntegration>, void>({
      query: () => ({
        url: '/v1/psp-integrations',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationsOrganizationsByName: build.query<
      { data: PspIntegrationOrganization[] },
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations/search/external-organizations${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getPspIntegrationsOrganizationsAccounts: build.query<
      { data: ExternalOrganizationAccount[] },
      string
    >({
      query: (query) => ({
        url: `/v1/psp-integrations-organizations/external-accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersExternalAccountWithIntegration: build.mutation<
      {
        data: ExternalAccount;
      },
      ExternalAccountWithIntegrationFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/generate-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindExternalOrganization: build.mutation<
      {
        data: LinkedClient;
      },
      ExternalOrganizationFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-organizations',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    bindExternalAccountToClientAccount: build.mutation<
      {
        data: { ok: true };
      },
      ExternalAccountToClientAccountFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-accounts/bind-account',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExternalAccountTopUpOptions: build.query<
      Response<ExternalAccountTopUpOption>,
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts?pspIntegrationAccountId=${data.id}&all=true${data.query ? data.query : ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExternalAccountChangeLogs: build.query<
      Response<ChangeLog>,
      string
    >({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerExternalAccountCurrencies: build.mutation<
      { data: ExternalAccount },
      { id: string; currenciesIds: string[] }
    >({
      query: (body) => ({
        url: `/v1/psp-integrations-accounts/${body.id}`,
        method: 'PUT',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExternalOrganizations: build.query<
      Response<LinkedClient>,
      { id: string; query?: string }
    >({
      query: ({ id, query }) => ({
        url: `/v1/clients-organizations/${id}/external-orgs${query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersRelationShipToCustomerDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/2f06e96f-b4cb-4112-9da6-4807b47f018d/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSourceOfWealthForCorporateDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/d16a207a-0909-41ee-87e6-5e4b88942bc5/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersBusinessActivitiesDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/368d47fb-2b48-415a-8abe-464f24747f84/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getRegulatedCompanyMainClientsDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/bb747e8c-4501-40b4-865e-21abfdc890e4/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSalesChannelsDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/822216b4-37cf-472b-b2fb-677c036dea5b/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSourceOfFunds: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/8d4eef94-ea66-4467-a8f8-bde787910cde/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSourceOfWealth: build.query<Response<DictionaryItem>, void>({
      query: () => ({
        url: '/v1/dictionaries/f27068ee-8e93-41f1-a2bc-4d5ab6f42a40/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAnnualIncomeDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/dcda46d0-c183-44fb-b0a7-1dab5797ee71/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountsCurrencies: build.query<
      Response<AccountsCurrencies>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/accounts-currencies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/accounts-currencies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/accounts-currencies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/accounts-currencies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAccountsFlows: build.query<Response<AccountsFlows>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/accounts-flows?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/accounts-flows`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/accounts-flows/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/accounts-flows/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersExpectedFundsOrigins: build.query<
      Response<ExpectedFundsOrigins>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/${id}/expected-funds-origins?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/${data.id}/expected-funds-origins`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-corporates/expected-funds-origins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomersExptectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-corporates/expected-funds-origins/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersTypeOfAccountsDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/26e90867-d430-437a-901f-84464e6ad26b/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSourceOfWealthForIndividualDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/66ee818d-132c-4a6e-b2ba-fea3405145f4/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersSourceOfIncomeDictionary: build.query<
      Response<DictionaryItem>,
      void
    >({
      query: () => ({
        url: '/v1/dictionaries/be30b804-9984-4281-a712-d9af90e310cc/items?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualCustomersAccountsCurrencies: build.query<
      Response<AccountsCurrencies>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/accounts-currencies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/accounts-currencies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      {
        id: string;
        body: AccountsCurrenciesFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/accounts-currencies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualCustomersAccountsCurrency: build.mutation<
      { data: AccountsCurrencies },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/accounts-currencies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualCustomersAccountsFlows: build.query<
      Response<AccountsFlows>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/accounts-flows?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/accounts-flows`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      {
        id: string;
        body: AccountsFlowsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/accounts-flows/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualCustomersAccountsFlow: build.mutation<
      { data: AccountsFlows },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/accounts-flows/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getIndividualCustomersExpectedFundsOrigins: build.query<
      Response<ExpectedFundsOrigins>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/expected-funds-origins?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createIndividualCustomersExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/expected-funds-origins`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateIndividualCustomersExpectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      {
        id: string;
        body: ExpectedFundsOriginsFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/expected-funds-origins/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteIndividualCustomersExptectedFundsOrigins: build.mutation<
      { data: ExpectedFundsOrigins },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-individual/expected-funds-origins/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersCRSAndFacta: build.query<Response<CRSAndFatca>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-individual/${id}/crs-and-fatca?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/${data.id}/crs-and-fatca`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-individual/crs-and-fatca/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomersCRSAndFacta: build.mutation<{ data: CRSAndFatca }, string>({
      query: (id) => ({
        url: `/v1/questionnaires-individual/crs-and-fatca/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCOFCustomersCRSAndFacta: build.query<Response<CRSAndFatca>, string>({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/${id}/crs-and-fatca?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCOFCustomersCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/${data.id}/crs-and-fatca`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCOFCustomersCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      {
        id: string;
        body: CRSAndFatcaFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/crs-and-fatca/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCOFCustomersCRSAndFacta: build.mutation<
      { data: CRSAndFatca },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/crs-and-fatca/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getCustomersAffilatedCompanies: build.query<
      Response<AffilatedCompany>,
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/${id}/affiliated-companies?all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomersAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      {
        id: string;
        body: AffilatedCompanyFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/${data.id}/affiliated-companies`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomersAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      {
        id: string;
        body: AffilatedCompanyFormValues;
      }
    >({
      query: (data) => ({
        url: `/v1/questionnaires-ubo/affiliated-companies/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomersAffilatedCompanies: build.mutation<
      { data: AffilatedCompany },
      string
    >({
      query: (id) => ({
        url: `/v1/questionnaires-ubo/affiliated-companies/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteCustomerPaymentAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/psp-integrations-payment-accounts/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createCustomerPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      ExternalAccountTopUpOptionFormValues
    >({
      query: (body) => ({
        url: '/v1/psp-integrations-payment-accounts',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateCustomerPaymentsAccounts: build.mutation<
      {
        data: ExternalAccountTopUpOption;
      },
      { id: string; body: ExternalAccountTopUpOptionFormValues }
    >({
      query: (data) => ({
        url: `/v1/psp-integrations-payment-accounts/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetCustomersTariffsListQuery,
  useAssignTariffToCustomerMutation,
  useCreateCustomFeeMutation,
  useDeleteCustomFeeMutation,
  useLazyGetTargetAccountQuery,
  useLazyGetCustomersAccountBalanceByIdQuery,
  useLazyGetCustomersBaseCurrencyQuery,
  useGetCustomersGLTransactionValidatorsQuery,
  useCreateCustomersGLTransactionMutation,
  useLazyGetCustomersAccountsListQuery,
  useGetCustomersAccountsCategoriesQuery,
  useLazyExportSelectedOverviewStatementsQuery,
  useLazyGetOverviewStatementsListQuery,
  useLazyGetTransactionAmlQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetSumSubTokenQuery,
  useLazyGetCustomerDocumentsQuery,
  useLazyGetCustomerStatsQuery,
  useAddCustomerDocumentsMutation,
  useLazyGetCustomerDocumentFileQuery,
  useDeleteCustomerDocumentMutation,
  useLazyGetCustomerDocumentQuery,
  useChangeCustomerDocumentsStatusMutation,
  useBatchDeleteCustomerDocumentsMutation,
  useChangeCustomerDocumentsCategoryMutation,
  useLazyGetCustomerIncomingTransactionQuery,
  useLazyGetCustomerIncomingTransactionWarningsQuery,
  useLazyGetCustomerOutgoingTransactionQuery,
  useLazyGetCustomerOutgoingTransactionWarningsQuery,
  useLazyGetCustomerInternalTransactionQuery,
  useLazyGetCustomerInternalTransactionWarningsQuery,
  useLazyGetCustomerGeneralLedgerTransactionQuery,
  useLazyGetCustomerExchangeTransactionQuery,
  useLazyGetCustomerNostroTransactionQuery,
  useLazyGetCustomersTransactionsPspPartnersQuery,
  useLazyGetCustomersGeneralLedgerStatmentQuery,
  useLazyGetCustomersAmlStatementQuery,
  useLazyGetCustomersInternalStatementQuery,
  useLazyGetCustomersLogStatementQuery,
  useLazyGetCustomersIncomingStatementQuery,
  useLazyGetCustomersAccountBalanceQuery,
  useLazyGetCustomersOutgoingStatementQuery,
  useLazyGetCustomersExchangeStatementQuery,
  useLazyGetCustomersNostroStatementQuery,
  useLazyGetCustomersTopUpQuery,
  useAddTopUpVisibilityMutation,
  useUpdateTopUpVisibilityMutation,
  useLazyGetCustomersTopUpInstructionsQuery,
  useLazyGetCustomerAgreementFileQuery,
  useLazyGetCustomerAgreementsQuery,
  useLazyGetCustomerUnsignedAgreementsQuery,
  useLazyGetCustomersTopUpInstructionPdfQuery,
  useLazyGetProblemTransactionChatsQuery,
  useCreateProblemTransactionChatMutation,
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useAddCommentToProblemTransactionDocumentMutation,
  useAddDocumentToProblemTransactionMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useDeleteProblemTransactionDocumentsMutation,
  useEditCommentInProblemTransactionDocumentMutation,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useUploadDocumentMutation,
  useLazyGetCustomerAgreementQuery,
  useResetCustomerPasswordMutation,
  useChangeDocumentExpirationDateMutation,
  useChangeBalanceStatusMutation,
  useLazyGetStatementItemsPdfQuery,
  useLazyGetTransactionInDocumentQuery,
  useGetOngoingMoitoringPdfMutation,
  useLazyGetCustomersStatementPdfQuery,
  useLazyGetCustomerBeneficiariesQuery,
  useLazyGetCustomerBeneficiaryQuery,
  useLazyGetCustomerBeneficiaryStatusLogQuery,
  useUpdateBeneficiaryStatusMutation,
  useLazyGetCustomerBeneficiaryContractsQuery,
  useLazyGetCustomerBeneficiaryContractQuery,
  useUpdateContractMutation,
  useUpdateContractStatusMutation,
  useLazyGetContractDocumentFileQuery,
  useLazyGetCustomerOrganizationAllowedDocumentsCategoriesQuery,
  useLazyGetAccountPrimariesQuery,
  useLazyGetCustomersExternalAccountsListQuery,
  useLazyGetCustomersPspPartnersQuery,
  useCreateCustomersExternalAccountMutation,
  useUpdateCustomersExternalAccountStatusMutation,
  useUpdateCustomersOverdraftBalanceValueMutation,
  useUpdateCustomersOverdraftStatusMutation,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
  useRenameCustomerDocumentMutation,
  useLazyGetTransactionStatementsChangeLogsQuery,
  useLazyGetCustomMarginsQuery,
  useLazyGetCustomersRateSourcesQuery,
  useCustomersCreateAmountsMutation,
  useLazyGetCustomerMarginQuery,
  useUpdateCustomerMarginMutation,
  useUpdateCustomerAmountMutation,
  useDeleteCustomerAmountMutation,
  useLazyGetCustomerDefaultMarginQuery,
  useRestoreCustomMarginToDefaultMutation,
  useLazyGetCustomersChangeLogsQuery,
  useCreateCustomerMarginMutation,
  useLazyGetCustomersBeneficairyDocumentsCategoriesQuery,
  useLazyGetBeneficiaryDocumentFileQuery,
  useUpdateCustomerQuestionnaireMutation,
  useLazyGetCustomersAccountsChangeLogsQuery,
  useCloseOrganizationMutation,
  useReopenOrganizationMutation,
  useLazyGetCustomersNotificationsListQuery,
  useLazyGetCustomersNotificationItemCommentQuery,
  useAddCustomersNotificationItemCommentMutation,
  useUpdateCustomersNotificationIssueMutation,
  useLazyGetCustomersNotificationChangeLogsQuery,
  useLazyGetCustomersNotificationApplicationEntityQuery,
  useLazyGetCustomersNotificationChannelEntityQuery,
  useLazyGetCustomersNotificationOrganizationEntityQuery,
  useLazyGetCustomersNotificationItemQuery,
  useBatchAddCustomersNotificationItemCommentMutation,
  useBatchUpdateCustomersNotificationIssueMutation,
  useLazyGetCustomersNotificationEmployeesQuery,
  useUpdateCustomersNotificationIssueCommentMutation,
  useLazyGetCustomerAllCreditAccountBalancesQuery,
  useLazyGetCustomerAllDebitAccountBalancesQuery,
  useLazyGetExpiredDocumentsQuery,
  useLazyGetAllTopUpVisibilityQuery,
  useLazyGetCustomerStatementChangeLogsQuery,
  useValidateCustomerBeneficiaryMutation,
  useLazyGetProblemTransactionCommentsQuery,
  useEditCommentInProblemTransactionMutation,
  useAddCommentToProblemTransactionMutation,
  useDeleteCommentInProblemTransactionMutation,
  useLazyGetCustomerCommentsQuery,
  useAddCommentToCustomerMutation,
  useDeleteCommentInCustomerMutation,
  useEditCommentInCustomerMutation,
  useLazyGetCustomerIncomingTransactionWarningQuery,
  useLazyGetCustomerInternalTransactionWarningQuery,
  useLazyGetCustomerOutgoingTransactionWarningQuery,
  useLazyGetCustomersExternalAccountQuery,
  useLazyGetCustomersAMLScoresHistoryQuery,
  useLazyGetCustomersAMLScoresHistoryByIdQuery,
  useLazyGetCustomersPspIntegrationsQuery,
  useCreateCustomersExternalAccountWithIntegrationMutation,
  useLazyGetPspIntegrationsOrganizationsByNameQuery,
  useBindExternalOrganizationMutation,
  useLazyGetPspIntegrationsOrganizationsAccountsQuery,
  useBindExternalAccountToClientAccountMutation,
  useLazyGetCustomersExternalAccountChangeLogsQuery,
  useLazyGetCustomersExternalAccountTopUpOptionsQuery,
  useUpdateCustomerExternalAccountCurrenciesMutation,
  useLazyGetCustomersExternalOrganizationsQuery,
  useLazyGetCustomersRelationShipToCustomerDictionaryQuery,
  useLazyGetCustomersSourceOfWealthForCorporateDictionaryQuery,
  useLazyGetCustomersBusinessActivitiesDictionaryQuery,
  useLazyGetRegulatedCompanyMainClientsDictionaryQuery,
  useLazyGetCustomersSalesChannelsDictionaryQuery,
  useLazyGetCustomersSourceOfFundsQuery,
  useLazyGetCustomersSourceOfWealthQuery,
  useLazyGetCustomersAnnualIncomeDictionaryQuery,
  useLazyGetCustomersAccountsCurrenciesQuery,
  useCreateCustomersAccountsCurrencyMutation,
  useUpdateCustomersAccountsCurrencyMutation,
  useDeleteCustomersAccountsCurrencyMutation,
  useLazyGetCustomersAccountsFlowsQuery,
  useCreateCustomersAccountsFlowMutation,
  useUpdateCustomersAccountsFlowMutation,
  useDeleteCustomersAccountsFlowMutation,
  useLazyGetCustomersExpectedFundsOriginsQuery,
  useCreateCustomersExpectedFundsOriginsMutation,
  useUpdateCustomersExpectedFundsOriginsMutation,
  useDeleteCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersTypeOfAccountsDictionaryQuery,
  useLazyGetCustomersSourceOfWealthForIndividualDictionaryQuery,
  useLazyGetCustomersSourceOfIncomeDictionaryQuery,
  useLazyGetIndividualCustomersAccountsCurrenciesQuery,
  useCreateIndividualCustomersAccountsCurrencyMutation,
  useUpdateIndividualCustomersAccountsCurrencyMutation,
  useDeleteIndividualCustomersAccountsCurrencyMutation,
  useLazyGetIndividualCustomersAccountsFlowsQuery,
  useCreateIndividualCustomersAccountsFlowMutation,
  useUpdateIndividualCustomersAccountsFlowMutation,
  useDeleteIndividualCustomersAccountsFlowMutation,
  useLazyGetIndividualCustomersExpectedFundsOriginsQuery,
  useCreateIndividualCustomersExpectedFundsOriginsMutation,
  useUpdateIndividualCustomersExpectedFundsOriginsMutation,
  useDeleteIndividualCustomersExptectedFundsOriginsMutation,
  useLazyGetCustomersCRSAndFactaQuery,
  useCreateCustomersCRSAndFactaMutation,
  useUpdateCustomersCRSAndFactaMutation,
  useDeleteCustomersCRSAndFactaMutation,
  useLazyGetCOFCustomersCRSAndFactaQuery,
  useCreateCOFCustomersCRSAndFactaMutation,
  useUpdateCOFCustomersCRSAndFactaMutation,
  useDeleteCOFCustomersCRSAndFactaMutation,
  useLazyGetCustomersAffilatedCompaniesQuery,
  useCreateCustomersAffilatedCompaniesMutation,
  useDeleteCustomersAffilatedCompaniesMutation,
  useUpdateCustomersAffilatedCompaniesMutation,
  useDeleteCustomerPaymentAccountsMutation,
  useCreateCustomerPaymentsAccountsMutation,
  useUpdateCustomerPaymentsAccountsMutation,
} = customersComponentApi;

export const {
  endpoints: {
    getCustomersTariffsList,
    assignTariffToCustomer,
    createCustomFee,
    deleteCustomFee,
    getTargetAccount,
    getCustomersAccountsCategories,
    getCustomersAccountsList,
    createCustomersGLTransaction,
    getCustomersGLTransactionValidators,
    getCustomersAccountBalanceById,
    getCustomersBaseCurrency,
    getOverviewStatementsList,
    exportSelectedOverviewStatements,
    getTransactionAml,
    getCustomersTransactionLogs,
    getSumSubToken,
    getCustomerDocuments,
    getCustomerStats,
    addCustomerDocuments,
    getCustomerDocumentFile,
    deleteCustomerDocument,
    getCustomerDocument,
    changeCustomerDocumentsStatus,
    batchDeleteCustomerDocuments,
    changeCustomerDocumentsCategory,
    getCustomerIncomingTransaction,
    getCustomerIncomingTransactionWarnings,
    getCustomerOutgoingTransaction,
    getCustomerOutgoingTransactionWarnings,
    getCustomerInternalTransaction,
    getCustomerInternalTransactionWarnings,
    getCustomerGeneralLedgerTransaction,
    getCustomerExchangeTransaction,
    getCustomerNostroTransaction,
    getCustomersTransactionsPspPartners,
    getCustomersGeneralLedgerStatment,
    getCustomersAmlStatement,
    getCustomersInternalStatement,
    getCustomersLogStatement,
    getCustomersIncomingStatement,
    getCustomersAccountBalance,
    getCustomersOutgoingStatement,
    getCustomersExchangeStatement,
    getCustomersNostroStatement,
    getCustomersTopUp,
    getAllTopUpVisibility,
    addTopUpVisibility,
    updateTopUpVisibility,
    getCustomersTopUpInstructions,
    getCustomerAgreementFile,
    getCustomerAgreements,
    getCustomerUnsignedAgreements,
    getCustomersTopUpInstructionPdf,
    getProblemTransactionChats,
    createProblemTransactionChat,
    getProblemTransactionRecipientClientsOfficias,
    getProblemTransactionSenderClientsOfficias,
    getProblemTransactionDocumentById,
    getProblemTransactionDocumentFile,
    getProblemTransactionsDocuments,
    uploadDocument,
    addCommentToProblemTransactionDocument,
    addDocumentToProblemTransaction,
    editCommentInProblemTransactionDocument,
    changeProblemTransactionDocumentCategory,
    changeProblemTransactionDocumentStatus,
    deleteCommentInProblemTransactionDocument,
    deleteProblemTransactionDocuments,
    getCustomerAgreement,
    resetCustomerPassword,
    changeDocumentExpirationDate,
    changeBalanceStatus,
    getStatementItemsPdf,
    getTransactionInDocument,
    getOngoingMoitoringPdf,
    getCustomersStatementPdf,
    getCustomerBeneficiaries,
    getCustomerBeneficiary,
    getCustomerBeneficiaryStatusLog,
    updateBeneficiaryStatus,
    getCustomerBeneficiaryContracts,
    getCustomerBeneficiaryContract,
    updateContract,
    updateContractStatus,
    getContractDocumentFile,
    getCustomerOrganizationAllowedDocumentsCategories,
    getAccountPrimaries,
    getCustomersExternalAccountsList,
    getCustomersPspPartners,
    createCustomersExternalAccount,
    updateCustomersExternalAccountStatus,
    updateCustomersOverdraftStatus,
    updateCustomersOverdraftBalanceValue,
    getCustomerOrganizationDocumentsStats,
    renameCustomerDocument,
    getTransactionStatementsChangeLogs,
    getCustomMargins,
    getCustomersChangeLogs,
    createCustomerMargin,
    updateCustomerQuestionnaire,
    getCustomersAccountsChangeLogs,
    closeOrganization,
    reopenOrganization,
    getCustomersNotificationsList,
    addCustomersNotificationItemComment,
    getCustomersNotificationItemComment,
    updateCustomersNotificationIssue,
    getCustomersNotificationChangeLogs,
    getCustomersNotificationApplicationEntity,
    getCustomersNotificationChannelEntity,
    getCustomersNotificationOrganizationEntity,
    getCustomersNotificationItem,
    batchUpdateCustomersNotificationIssue,
    batchAddCustomersNotificationItemComment,
    getCustomersNotificationEmployees,
    updateCustomersNotificationIssueComment,
    getCustomerAllCreditAccountBalances,
    getCustomerAllDebitAccountBalances,
    getExpiredDocuments,
    getCustomerStatementChangeLogs,
    validateCustomerBeneficiary,
    addCommentToProblemTransaction,
    editCommentInProblemTransaction,
    deleteCommentInProblemTransaction,
    getProblemTransactionComments,
    getCustomerComments,
    addCommentToCustomer,
    editCommentInCustomer,
    deleteCommentInCustomer,
    getCustomerIncomingTransactionWarning,
    getCustomerOutgoingTransactionWarning,
    getCustomerInternalTransactionWarning,
    getCustomersExternalAccount,
    getCustomersAMLScoresHistory,
    getCustomersAMLScoresHistoryById,
    getCustomersPspIntegrations,
    createCustomersExternalAccountWithIntegration,
    getPspIntegrationsOrganizationsByName,
    bindExternalOrganization,
    getPspIntegrationsOrganizationsAccounts,
    bindExternalAccountToClientAccount,
    getCustomersExternalAccountChangeLogs,
    getCustomersExternalAccountTopUpOptions,
    updateCustomerExternalAccountCurrencies,
    getCustomersExternalOrganizations,
    getCustomersRelationShipToCustomerDictionary,
    getCustomersSourceOfWealthForCorporateDictionary,
    getCustomersBusinessActivitiesDictionary,
    getRegulatedCompanyMainClientsDictionary,
    getCustomersSalesChannelsDictionary,
    getCustomersSourceOfFunds,
    getCustomersSourceOfWealth,
    getCustomersAnnualIncomeDictionary,
    getCustomersAccountsCurrencies,
    createCustomersAccountsCurrency,
    deleteCustomersAccountsCurrency,
    updateCustomersAccountsCurrency,
    getCustomersAccountsFlows,
    updateCustomersAccountsFlow,
    createCustomersAccountsFlow,
    deleteCustomersAccountsFlow,
    getCustomersExpectedFundsOrigins,
    createCustomersExpectedFundsOrigins,
    updateCustomersExpectedFundsOrigins,
    deleteCustomersExptectedFundsOrigins,
    getCustomersTypeOfAccountsDictionary,
    getCustomersSourceOfWealthForIndividualDictionary,
    getCustomersSourceOfIncomeDictionary,
    getIndividualCustomersAccountsCurrencies,
    createIndividualCustomersAccountsCurrency,
    updateIndividualCustomersAccountsCurrency,
    deleteIndividualCustomersAccountsCurrency,
    getIndividualCustomersAccountsFlows,
    createIndividualCustomersAccountsFlow,
    updateIndividualCustomersAccountsFlow,
    deleteIndividualCustomersAccountsFlow,
    getIndividualCustomersExpectedFundsOrigins,
    createIndividualCustomersExpectedFundsOrigins,
    updateIndividualCustomersExpectedFundsOrigins,
    deleteIndividualCustomersExptectedFundsOrigins,
    getCustomersCRSAndFacta,
    createCustomersCRSAndFacta,
    updateCustomersCRSAndFacta,
    deleteCustomersCRSAndFacta,
    getCOFCustomersCRSAndFacta,
    createCOFCustomersCRSAndFacta,
    updateCOFCustomersCRSAndFacta,
    deleteCOFCustomersCRSAndFacta,
    getCustomersAffilatedCompanies,
    createCustomersAffilatedCompanies,
    updateCustomersAffilatedCompanies,
    deleteCustomersAffilatedCompanies,
    deleteCustomerAmount,
    createCustomerPaymentsAccounts,
    updateCustomerPaymentsAccounts,
  },
} = customersComponentApi;
